import type {
  PraxaDesktopConnectedWorkItem,
  PraxaDesktopConnectedWorkKind,
  PraxaDesktopConnectedWorkProvider,
  PraxaDesktopConnectedWorkProviderHealth,
} from "@praxa/platform-contracts";

export type PraxaConnectedWorkSection =
  | "inbox"
  | "my_work"
  | "docs"
  | "activity"
  | "search"
  | "pinned";

export type PraxaConnectedWorkProviderFilter = "all" | PraxaDesktopConnectedWorkProvider;

export type PraxaConnectedWorkProviderIndicatorStatus =
  | "authentication_required"
  | "connected"
  | "not_connected";

export interface PraxaConnectedWorkProviderIndicator {
  readonly label: string;
  readonly status: PraxaConnectedWorkProviderIndicatorStatus;
}

export const PRAXA_CONNECTED_WORK_SECTIONS = [
  { id: "inbox", label: "Inbox" },
  { id: "my_work", label: "My Work" },
  { id: "docs", label: "Docs" },
  { id: "activity", label: "Activity" },
  { id: "search", label: "Search" },
  { id: "pinned", label: "Pinned" },
] as const satisfies ReadonlyArray<{
  readonly id: PraxaConnectedWorkSection;
  readonly label: string;
}>;

export const PRAXA_CONNECTED_WORK_PROVIDERS = [
  { id: "slack", label: "Slack" },
  { id: "linear", label: "Linear" },
  { id: "notion", label: "Notion" },
] as const satisfies ReadonlyArray<{
  readonly id: PraxaDesktopConnectedWorkProvider;
  readonly label: string;
}>;

const KIND_LABELS: Record<PraxaDesktopConnectedWorkKind, string> = {
  slack_channel: "Channel",
  linear_issue: "Issue",
  linear_project: "Project",
  notion_page: "Page",
  notion_database: "Database",
};

export function connectedWorkProviderLabel(provider: PraxaDesktopConnectedWorkProvider): string {
  return PRAXA_CONNECTED_WORK_PROVIDERS.find((option) => option.id === provider)?.label ?? provider;
}

export function connectedWorkKindLabel(kind: PraxaDesktopConnectedWorkKind): string {
  return KIND_LABELS[kind];
}

function itemMatchesSection(
  item: PraxaDesktopConnectedWorkItem,
  section: PraxaConnectedWorkSection,
): boolean {
  switch (section) {
    case "inbox":
      return item.signals.inbox;
    case "my_work":
      return item.signals.myWork;
    case "docs":
      return item.signals.docs;
    case "activity":
      return item.signals.activity;
    case "pinned":
      return item.pinned;
    case "search":
      return true;
  }
}

function itemMatchesQuery(item: PraxaDesktopConnectedWorkItem, query: string): boolean {
  const normalizedQuery = query.trim().toLocaleLowerCase();
  if (!normalizedQuery) return false;
  return [
    item.title,
    item.summary,
    item.context,
    connectedWorkProviderLabel(item.provider),
    connectedWorkKindLabel(item.kind),
  ].some((value) => value.toLocaleLowerCase().includes(normalizedQuery));
}

export function filterConnectedWorkItems(
  items: ReadonlyArray<PraxaDesktopConnectedWorkItem>,
  input: {
    readonly provider: PraxaConnectedWorkProviderFilter;
    readonly query: string;
    readonly section: PraxaConnectedWorkSection;
  },
): ReadonlyArray<PraxaDesktopConnectedWorkItem> {
  return items
    .filter((item) => input.provider === "all" || item.provider === input.provider)
    .filter((item) => itemMatchesSection(item, input.section))
    .filter((item) => input.section !== "search" || itemMatchesQuery(item, input.query))
    .toSorted((left, right) => Date.parse(right.updatedAt) - Date.parse(left.updatedAt));
}

export function hasAnyConnectedWorkProvider(
  providers: ReadonlyArray<PraxaDesktopConnectedWorkProviderHealth>,
): boolean {
  return providers.some(
    (provider) =>
      provider.connectionId !== null &&
      provider.status !== "not_connected" &&
      provider.status !== "needs_authorization" &&
      provider.status !== "permission_lost",
  );
}

export function connectedWorkProviderIndicator(
  provider: PraxaDesktopConnectedWorkProviderHealth | undefined,
): PraxaConnectedWorkProviderIndicator {
  if (
    provider === undefined ||
    provider.status === "not_connected" ||
    (provider.status === "needs_authorization" && provider.connectionId === null)
  ) {
    return { label: "Not connected", status: "not_connected" };
  }
  if (provider.status === "needs_authorization" || provider.status === "permission_lost") {
    return { label: "Authentication required", status: "authentication_required" };
  }
  switch (provider.status) {
    case "partial":
      return { label: "Connected — limited access", status: "connected" };
    case "rate_limited":
      return { label: "Connected — temporarily rate limited", status: "connected" };
    case "error":
      return { label: "Connected — refresh failed", status: "connected" };
    case "ready":
      return { label: "Connected", status: "connected" };
  }
}

export function authorizedConnectedWorkItems(
  items: ReadonlyArray<PraxaDesktopConnectedWorkItem>,
  providers: ReadonlyArray<PraxaDesktopConnectedWorkProviderHealth>,
): ReadonlyArray<PraxaDesktopConnectedWorkItem> {
  return items.filter((item) => {
    const health = providers.find((provider) => provider.provider === item.provider);
    return (
      health?.connectionId === item.connectionId &&
      health.status !== "not_connected" &&
      health.status !== "needs_authorization" &&
      health.status !== "permission_lost"
    );
  });
}

export function formatConnectedWorkTimestamp(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown time";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}
